import React from 'react';
import { Card, Badge, Button, Row, Col, Alert } from 'react-bootstrap';
import VersionIndicator from './VersionIndicator';
import useVersionCheck from '../hooks/useVersionCheck';

const VersionInfo = ({ className = '' }) => {
    const {
        checkVersion,
        isOutdated,
        isChecking,
        currentVersion,
        serverVersion,
        lastCheckTime,
        error
    } = useVersionCheck({
        enableAutoCheck: false, // Don't auto-check in this component
        checkOnMount: false     // Don't check on mount
    });

    const handleCheckVersion = () => {
        checkVersion();
    };

    const formatDateTime = (date) => {
        if (!date) return 'Never';
        return date.toLocaleString();
    };

    return (
        <Card className={`version-info ${className}`}>
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        Version Information
                    </h6>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleCheckVersion}
                        disabled={isChecking}
                    >
                        {isChecking ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Checking...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-arrow-repeat me-1"></i>
                                Check for Updates
                            </>
                        )}
                    </Button>
                </div>
            </Card.Header>

            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label text-muted">Current Version</label>
                            <div className="d-flex align-items-center">
                                <VersionIndicator
                                    isOutdated={isOutdated}
                                    isChecking={isChecking}
                                    lastCheckTime={lastCheckTime}
                                    showBuildNumber={true}
                                    size="md"
                                />
                            </div>
                        </div>
                    </Col>

                    <Col md={6}>
                        <div className="mb-3">
                            <label className="form-label text-muted">Latest Version</label>
                            <div>
                                {serverVersion ? (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="success" className="me-2">
                                            v{serverVersion.version}
                                        </Badge>
                                        <Badge bg="success">
                                            #{serverVersion.buildNumber}
                                        </Badge>
                                    </div>
                                ) : (
                                    <span className="text-muted">Click "Check for Updates"</span>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <div className="mb-3">
                            <label className="form-label text-muted">Last Check</label>
                            <div className="text-muted small">
                                {formatDateTime(lastCheckTime)}
                            </div>
                        </div>
                    </Col>
                </Row>

                {isOutdated && (
                    <Alert variant="warning" className="mb-2">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Update Available:</strong> A newer version of the application is available.
                        Please refresh your browser to get the latest features and security updates.
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" className="mb-2">
                        <i className="bi bi-exclamation-circle me-2"></i>
                        <strong>Check Failed:</strong> {error}
                    </Alert>
                )}

                <div className="text-muted small">
                    <p className="mb-1">
                        <strong>Build Information:</strong>
                    </p>
                    <ul className="mb-0 ps-3">
                        <li>Application: {process.env.REACT_APP_TITLE}</li>
                        <li>Environment: {process.env.NODE_ENV}</li>
                        <li>Company: {process.env.REACT_APP_COMPANY_NAME}</li>
                    </ul>
                </div>
            </Card.Body>
        </Card>
    );
};

export default VersionInfo;